import { Badge } from "@/components/ui";
import { useIsMobile } from "@/hooks/use-mobile";
import {
  cn,
  formatAmount,
  getRequiredFields,
  invoiceParkStatus,
  toApiSort,
} from "@/lib/utils";
import { RootState } from "@/redux/store";
import { routes } from "@/routes/utils";
import { useGetPostedDocumentList } from "@/services";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { formValues, schema } from "./validations";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  parkDocumentSearch,
  SEARCH_CRITERIA,
  SEARCH_TYPE,
  searchType,
} from "./consts";

const useInvoiceListing = () => {
  // const { glId, month } = useParams();
  const [globalFilter, setGlobalFilter] = useState<string>("");
  const [pageNumber, setPageNumber] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(10);
  const [sorting, setSorting] = useState([{ id: "createdAt", desc: true }]);
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const form = useForm<formValues>({
    defaultValues: {
      [SEARCH_TYPE]: "",
      [SEARCH_CRITERIA]: "",
    },
    resolver: zodResolver(schema),
  });
  const requiredFields = getRequiredFields(schema);

  const communityDetail = useSelector(
    (state: RootState) => state.currentData.communityData
  );
  const currentFinanceYear = useSelector(
    (state: RootState) => state.currentData.currentFinanceYear
  );

  const {
    data: documentList,
    isLoading: isDocLoading,
    refetch: refetchDoc,
    isRefetching: isDocRefetching,
  } = useGetPostedDocumentList({
    data: {
      filters: [
        {
          field: "year",
          value: currentFinanceYear,
          operator: "EQUAL",
        },
        {
          field: "communityId",
          value: communityDetail?.communityId,
          operator: "EQUAL",
        },
        {
          field: "documentType",
          value: "VI",
          operator: "EQUAL",
        },
        ...(form.watch()[SEARCH_TYPE]
          ? [
              {
                field: form.watch()[SEARCH_TYPE],
                value: form.watch()[SEARCH_CRITERIA],
                operator:
                  (
                    [searchType, parkDocumentSearch]?.find(
                      (data: any) =>
                        data.value == (form.watch()[SEARCH_TYPE] as string)
                    ) as any
                  )?.operator || "EQUAL",
              },
            ]
          : []),
        {
          field: "status",
          value: ["APPROVED", "POSTED"],
          operator: "CONTAINS_ANY",
        },
        // {
        //   field: "glAccountId",
        //   value: glId,
        //   operator: "EQUAL",
        // },
        // {
        //   field: "budgetMonth",
        //   value: month,
        //   operator: "EQUAL",
        // },
      ],
      pageNumber,
      pageSize,
      sort: toApiSort(sorting, { field: "createdAt", direction: "DESC" }),
    },
  });

  useEffect(() => {
    refetchDoc();
  }, [pageNumber, pageSize, sorting]);

  useEffect(() => {
    if (globalFilter?.length >= 2 || globalFilter?.length == 0) {
      refetchDoc();
    }
  }, [globalFilter]);

  const onSubmit = () => {
    refetchDoc();
  };

  const onClear = () => {
    form.reset();
    setTimeout(() => refetchDoc(), 100);
  };

  const breadcrumbData = useMemo(
    () => [
      {
        link: `/${routes.cam}/${routes.activityDashboard}`,
        name: `Activity Dashboard - ${communityDetail?.name || ""}`,
      },
      {
        link: `/${routes.cam}/${routes.activityDashboard}/${routes.payment}/${routes.reports}`,
        name: <span>Payment 360 Report</span>,
      },
      {
        link: "",
        name: <span className="text-primary">View Invoice</span>,
      },
    ],
    []
  );

  const onBackClick = () => {
    navigate(
      `/${routes.cam}/${routes.activityDashboard}/${routes.payment}/${routes.reports}`
    );
  };

  // Define Table Columns
  const columns = [
    {
      accessorKey: "parkDocumentHeaderId",
      header: (
        <div>
          Parked <br /> Doc. ID
        </div>
      ),
      accessorFn: (row: any) => String(row.parkDocumentHeaderId || ""),
      size: 50,
    },
    {
      accessorKey: "year",
      header: <div>Year</div>,
      accessorFn: (row: any) => String(row.year),
      size: 80,
    },
    {
      accessorKey: "vendorName",
      header: <div>Vendor Name</div>,
      accessorFn: (row: any) => String(row?.vendorName || ""),
    },
    {
      accessorKey: "invoiceNumber",
      header: <div>Invoice No.</div>,
      accessorFn: (row: any) => String(row?.invoiceNumber) || "",
    },
    {
      accessorKey: "invoiceAmount",
      meta: { align: "right" },
      header: <div>Invoice Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.invoiceAmount || 0),
    },
    {
      accessorKey: "approvedAmount",
      meta: { align: "right" },
      header: <div>Approved Amt.</div>,
      accessorFn: (row: any) => formatAmount(row?.approvedAmount || 0),
    },
    {
      accessorKey: "status",
      header: <div>Status</div>,
      cell: (row: any) => (
        <Badge
          variant="outline"
          className={cn(
            invoiceParkStatus[row.row.original.status?.toLowerCase()]?.color,
            "rounded-full px-4 py-2"
          )}
        >
          {invoiceParkStatus[row?.row?.original?.status?.toLowerCase()]?.label}
        </Badge>
      ),
    },
    {
      accessorKey: "submittedOn",
      header: <div>Submitted On</div>,
      accessorFn: (row: any) =>
        moment(row?.submittedOn || "").format("MM/DD/YYYY"),
    },
  ];

  const onRowClick = (row: any) => {
    navigate(`${row.original.parkDocumentHeaderId}/view`);
  };

  return {
    breadcrumbData,
    columns,
    globalFilter,
    setGlobalFilter,
    isMobile,
    onRowClick,
    isDocLoading,
    documentList,
    isDocRefetching,
    setPageNumber,
    setPageSize,
    pageNumber,
    pageSize,
    sorting,
    setSorting,
    form,
    requiredFields,
    onSubmit,
    onClear,
    onBackClick,
    communityDetail,
  };
};

export default useInvoiceListing;
